/**
 * UnlockReturn (WP-F11a) — the landing route for the Stripe hosted-Checkout return.
 *
 * The success/cancel URLs are STATIC server config (`Stripe:SuccessUrl` / `Stripe:CancelUrl`, WP-B20)
 * and cannot carry the reference id, so this thin route reads which reference the round-trip belonged
 * to from the pending stash (set just before the redirect) and forwards to that reference's detail
 * screen with a `?checkout=success|cancelled` flag — where the entitlement is reconciled and the seal
 * ceremony plays. With no stash (a stale or deep-linked return) the ledger is the safe home: a paid
 * unlock still reflects there once the webhook lands.
 *
 * WP-I14 must configure the Stripe redirect URLs to point here, e.g.
 *   Stripe:SuccessUrl = {app}/unlock/return?status=success&session_id={CHECKOUT_SESSION_ID}
 *   Stripe:CancelUrl  = {app}/unlock/return?status=cancelled
 */
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { ThreadMark } from '@/components/ThreadMark';
import { parseCheckoutReturn, readPendingUnlock } from '@/lib/unlock';

export function UnlockReturn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    // Absent/garbled status is treated as a cancel — the conservative reading (never imply a charge).
    const outcome = parseCheckoutReturn(params.get('status')) ?? 'cancelled';
    const pending = readPendingUnlock();
    navigate(pending ? `/requests/${pending.referenceId}?checkout=${outcome}` : '/', { replace: true });
  }, [navigate, params]);

  return (
    <AppShell title="Reference">
      <div className="flex flex-1 flex-col items-center justify-center py-20" aria-live="polite" aria-busy="true">
        <ThreadMark className="brand-pulse h-6 w-auto" />
        <p className="mt-3 font-body text-[13px] text-ink-quiet">Taking you back to your reference…</p>
      </div>
    </AppShell>
  );
}
